import React, { SyntheticEvent } from 'react';
import cc from 'classcat';
import { IAlert } from '../../../libs/alert';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import Tooltip from '../tooltip';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FormatDaysAndClassesAgo } from '../../../libs/time';
import AnchorButton from '../../molecules/button/anchor';
import { useActiveDialogsUpdater } from '../../../contexts/active-dialogs';
import { IPatient } from '../../../libs/patient';

/**
 * This action kicks off the resolve
 * alert flow
 *
 */

interface ResolveAlertActionProps {
  alert: IAlert;
  onResolved?: () => void;
}

export const ResolveAlertAction = ({
  alert,
  onResolved
}: ResolveAlertActionProps): JSX.Element => {
  const addDialog = useActiveDialogsUpdater();
  const output = FormatDaysAndClassesAgo(
    alert.readAt ? alert.readAt : alert.createdAt
  );
  const isDisabled = alert.readAt ? true : false;
  let title = 'Resolve';
  const classNames = [];

  if (alert.readAt) {
    title = output.naturalDate;
    classNames.push(output.className);
  }

  const handleClick = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    addDialog({
      id: `resolveAlert${alert.id}`,
      name: 'AddressEvents',
      payload: {
        addressType: 'resolve',
        alert,
        patientId: alert.patientId,
        onSuccess: () => {
          if (onResolved) onResolved();
        }
      }
    });
  };

  return (
    <AnchorButton
      className={cc(classNames)}
      onClick={handleClick}
      disabled={isDisabled}
      variant="minimal"
      size="small"
    >
      <Icon icon={faCheck} title={title} />
    </AnchorButton>
  );
};

/**
 * This action kicks off the resolve
 * patient flow
 */

interface ResolvePatientActionProps {
  patient: IPatient;
  compact?: boolean;
}

export const ResolvePatientAction = ({
  patient,
  compact = false
}: ResolvePatientActionProps): JSX.Element => {
  const addDialog = useActiveDialogsUpdater();
  const output = FormatDaysAndClassesAgo(
    patient.addressedAt ? patient.addressedAt : patient.createdAt
  );
  let title = 'Resolve';
  const classNames = [];

  if (patient.addressedAt) {
    title = output.naturalDate;
    classNames.push(output.className);
  }

  const handleClick = () => {
    addDialog({
      id: `resolvePatient${patient.id}`,
      name: 'AddressEvents',
      payload: {
        addressType: 'resolve',
        patientId: patient.id
      }
    });
  };

  const button = (
    <AnchorButton
      className={cc(classNames)}
      onClick={handleClick}
      variant="minimal"
    >
      <Icon
        className={compact ? undefined : 'mr-2'}
        icon={faCheck}
        title={title}
      />
      {!compact && (
        <Text className="text-small" dotted>
          <span role="status">{title}</span>
        </Text>
      )}
    </AnchorButton>
  );

  if (compact) {
    return <Tooltip content={title}>{button}</Tooltip>;
  }

  return button;
};
